import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, BarChart2, Home as HomeIcon, Map, User, LogOut, Calendar, Bug } from 'lucide-react';
import Logo from './Logo';
import HelpTrigger from '../../help/components/HelpTrigger';
import { getRouteHelpTarget } from '../../help/utils/routeHelpMap';
import useAppUser from '../../../hooks/useAppUser';
import { useUser } from '../../../contexts/UserContext';
import SupportTicketDialog from '../../support/components/SupportTicketDialog';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const routeSignature = `${location.pathname}${location.hash}`;
  const { firebaseUser, role, loading } = useAppUser();
  const { logout } = useUser();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [menuRouteSignature, setMenuRouteSignature] = useState(routeSignature);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/', icon: HomeIcon, show: true },
    { name: 'Expéditions', path: '/#sorties', icon: Map, show: true },
    { name: 'Mes Réservations', path: '/mes-reservations', icon: Calendar, show: Boolean(firebaseUser) },
    { name: 'Admin', path: '/admin', icon: BarChart2, show: ['Admin', 'Patron', 'Developpeur'].includes(String(role || '')) },
  ];

  const routeHelpTarget = getRouteHelpTarget(location.pathname);
  const hasContextualHelp = Boolean(routeHelpTarget.articleId || routeHelpTarget.category);
  const isElevatedUser = ['Admin', 'Patron', 'Developpeur'].includes(String(role || ''));
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isMobileMenuVisible = mobileMenuOpen && menuRouteSignature === routeSignature;
  const isDropdownVisible = dropdownOpen && menuRouteSignature === routeSignature;
  const useCompactNavbar = isScrolled || isAdminRoute || isMobileMenuVisible;

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMenuRouteSignature(routeSignature);
  };

  const handleMobileMenuToggle = () => {
    setMenuRouteSignature(routeSignature);
    setMobileMenuOpen((previous) => !previous);
  };

  const handleDropdownToggle = () => {
    setMenuRouteSignature(routeSignature);
    setDropdownOpen((previous) => !previous);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-700 ${
        useCompactNavbar
          ? 'pt-[max(12px,env(safe-area-inset-top))] pb-3 px-4 sm:px-6 md:px-12'
          : 'pt-[max(24px,env(safe-area-inset-top))] pb-6 px-4 sm:px-6 md:px-12'
      }`}
    >
      <div className={`max-w-7xl mx-auto rounded-full transition-all duration-700 border ${
        useCompactNavbar
          ? 'bg-white/90 backdrop-blur-2xl border-brand-primary/5 shadow-[0_8px_32px_rgba(0,0,0,0.05)] px-4 sm:px-6 md:px-8 py-3'
          : 'bg-transparent border-transparent px-0 py-0'
      }`}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <Logo iconOnly className={`w-8 h-8 transition-all duration-500 ${useCompactNavbar ? 'scale-90' : 'scale-110'}`} />
            <span className={`font-logo text-xs transition-opacity duration-500 ${useCompactNavbar ? 'opacity-100' : 'opacity-0'} hidden md:block`}>
              jacana
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 justify-center items-center gap-12">
            {navLinks.filter(link => link.show).map((link) => {
              const isActive = link.path === '/#sorties'
                ? location.pathname === '/' && location.hash === '#sorties'
                : location.pathname === link.path;

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] transition-all duration-300 hover:text-brand-accent relative group ${
                    isActive ? 'text-brand-accent' : 'text-brand-primary/60'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 right-0 h-px bg-brand-accent transition-transform duration-500 origin-left ${
                    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`} />
                </Link>
              );
            })}
          </div>

          {/* Auth Button Desktop */}
          <div className="hidden md:flex items-center justify-end gap-3 min-w-[180px] relative" ref={dropdownRef}>
            {hasContextualHelp ? (
              <HelpTrigger
                articleId={routeHelpTarget.articleId}
                category={routeHelpTarget.category}
                label="Aide sur cette page"
              />
            ) : null}
            {!loading && (
              firebaseUser ? (
                <div>
                  <button 
                    onClick={handleDropdownToggle}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-primary/10 hover:border-brand-primary/30 text-sm font-medium transition-all min-h-[40px]"
                  >
                    <User size={14} />
                    <span className="max-w-[100px] truncate">{firebaseUser.displayName || firebaseUser.email}</span>
                  </button>

                  <AnimatePresence>
                    {isDropdownVisible && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-brand-primary/5 py-2 overflow-hidden"
                      >
                        <Link 
                          to="/mes-reservations" 
                          onClick={closeMenus}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-brand-primary/80 hover:bg-brand-paper hover:text-brand-primary transition-colors"
                        >
                          <Calendar size={16} />
                          Mes Réservations
                        </Link>
                        <Link 
                          to="/mes-consentements" 
                          onClick={closeMenus}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-brand-primary/80 hover:bg-brand-paper hover:text-brand-primary transition-colors"
                        >
                          <User size={16} />
                          Mes consentements
                        </Link>
                        <button 
                          onClick={() => {
                            closeMenus();
                            setSupportDialogOpen(true);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-primary/80 hover:bg-brand-paper hover:text-brand-primary transition-colors"
                        >
                          <Bug size={16} />
                          Aide / signalement
                        </button>
                        <button 
                          onClick={logout}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut size={16} />
                          Déconnexion
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/login" className="btn-premium px-6 py-2 text-[11px] md:text-xs min-h-[40px]">
                  SE CONNECTER
                </Link>
              )
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2 ml-auto shrink-0">
            {hasContextualHelp ? (
              <HelpTrigger
                articleId={routeHelpTarget.articleId}
                category={routeHelpTarget.category}
                label="Aide sur cette page"
              />
            ) : null}
            <button 
              onClick={handleMobileMenuToggle}
              className="flex items-center justify-center rounded-full p-3 text-brand-primary/70 hover:text-brand-primary hover:bg-brand-paper transition-colors min-h-[44px] min-w-[44px]"
            >
              {isMobileMenuVisible ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuVisible && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden absolute top-full left-4 right-4 sm:left-6 sm:right-6 mt-3 bg-white/95 backdrop-blur-2xl rounded-[28px] border border-brand-primary/5 shadow-2xl overflow-hidden"
          >
            <div className="p-6 flex flex-col gap-5">
              {navLinks.filter(link => link.show).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={closeMenus}
                  className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-primary/75 hover:text-brand-accent transition-all min-h-[44px]"
                >
                  <link.icon size={16} className="opacity-40" />
                  {link.name}
                </Link>
              ))}

              <div className="h-px bg-brand-primary/10 my-2" />

              {!loading && (
                firebaseUser ? (
                  <>
                    <Link
                      to="/mes-reservations"
                      onClick={closeMenus}
                      className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-primary/75 hover:text-brand-accent transition-all min-h-[44px]"
                    >
                      <Calendar size={16} className="opacity-40" />
                      Mes Réservations
                    </Link>
                    <Link
                      to="/mes-consentements"
                      onClick={closeMenus}
                      className="flex items-center gap-6 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-primary/75 hover:text-brand-accent transition-all min-h-[44px]"
                    >
                      <User size={16} className="opacity-40" />
                      Mes consentements
                    </Link>
                    <button
                      onClick={() => {
                        closeMenus();
                        setSupportDialogOpen(true);
                      }}
                      className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-primary/75 hover:text-brand-accent transition-all w-full text-left min-h-[44px]"
                    >
                      <Bug size={16} className="opacity-40" />
                      Aide / signalement
                    </button>
                    <button
                      onClick={logout}
                      className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.16em] text-red-500/85 hover:text-red-600 transition-all w-full text-left min-h-[44px]"
                    >
                      <LogOut size={16} className="opacity-40" />
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={closeMenus}
                    className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-primary hover:text-brand-accent transition-all min-h-[44px]"
                  >
                    <User size={16} className="opacity-40" />
                    Se connecter
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SupportTicketDialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
        variant={isElevatedUser ? 'admin' : 'user'}
        role={role}
        moduleHint="navbar"
      />
    </nav>
  );
}
